'use client';
import React from 'react';
import {
  Controller,
  useFormContext,
  Path,
  FieldValues,
  ControllerRenderProps,
} from 'react-hook-form';
import { FiCheck } from 'react-icons/fi';

import { Icon, Box, Text, Spinner } from '../../index';
import { useAsyncValidation } from '../../hooks/use-form-validation';
import { Field } from '../form';
import { Input, InputGroup, InputLeftElement, InputRightElement } from '../input';
import { InputNumber } from '../input-number';
import { Textarea } from '../textarea';
import { Select } from '../select';
import { Checkbox } from '../checkbox';
import { RadioGroup } from '../radio';

interface ValidatedFieldProps {
  field: {
    name: string;
    label?: string;
    type: string;
    placeholder?: string;
    helperText?: string;
    required?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    options?: { value: string; label: string }[];
    defaultValue?: unknown;
    leftIcon?: React.ReactElement;
    rightIcon?: React.ReactElement;
    min?: number;
    max?: number;
    step?: number;
    validateOnChange?: boolean;
    validateOnBlur?: boolean;
    asyncValidator?: (value: unknown) => Promise<boolean | { isValid: boolean; message?: string }>;
    debounceMs?: number;
    showSuccess?: boolean;
  };
}

export function ValidatedField<T extends FieldValues>({ field }: ValidatedFieldProps) {
  const {
    control,
    formState: { errors },
    trigger,
    setError,
    clearErrors,
  } = useFormContext<T>();

  // Setup async validation - always call hook, but make validator conditional
  const asyncValidation = useAsyncValidation(
    field.asyncValidator || (() => Promise.resolve(true)),
    {
      debounceMs: field.debounceMs || 300,
    }
  );

  // Handle real-time validation
  const handleValidation = async (value: unknown, fieldName: string) => {
    // First run sync validation
    const isValid = await trigger(fieldName as Path<T>);

    // Then run async validation if provided and sync validation passed
    if (isValid && field.asyncValidator) {
      const asyncResult = await asyncValidation.validate(value);
      if (!asyncResult) {
        setError(fieldName as Path<T>, {
          message: asyncValidation.error || 'Validation failed',
        });
        return false;
      } else {
        clearErrors(fieldName as Path<T>);
        return true;
      }
    }

    return isValid;
  };

  const renderFieldContent = (controllerField: ControllerRenderProps<T, Path<T>>) => {
    const showSuccessIcon =
      field.showSuccess &&
      !errors[field.name] &&
      !asyncValidation.isValidating &&
      controllerField.value;

    const rightElement =
      field.asyncValidator && asyncValidation.isValidating ? (
        <Spinner size="sm" data-testid="spinner" />
      ) : showSuccessIcon ? (
        <Icon size="sm" color="#10B981">
          <FiCheck />
        </Icon>
      ) : field.rightIcon ? (
        <Icon iconColor="secondary" size="sm">
          {field.rightIcon}
        </Icon>
      ) : null;

    switch (field.type) {
      case 'text':
      case 'email':
      case 'password': {
        const inputElement = (
          <Input
            {...controllerField}
            type={field.type}
            placeholder={field.placeholder}
            disabled={field.disabled}
            readOnly={field.readOnly}
            onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
              controllerField.onChange(e);
              if (field.validateOnChange) {
                await handleValidation(e.target.value, field.name);
              }
            }}
            onBlur={async (e: React.FocusEvent<HTMLInputElement>) => {
              controllerField.onBlur();
              if (field.validateOnBlur !== false) {
                await handleValidation(e.target.value, field.name);
              }
            }}
          />
        );

        if (field.leftIcon || rightElement) {
          return (
            <InputGroup>
              {field.leftIcon && (
                <InputLeftElement>
                  <Icon iconColor="secondary" size="sm">
                    {field.leftIcon}
                  </Icon>
                </InputLeftElement>
              )}
              {inputElement}
              {rightElement && <InputRightElement>{rightElement}</InputRightElement>}
            </InputGroup>
          );
        }
        return inputElement;
      }

      case 'number':
        return (
          <Box position="relative">
            <InputNumber
              {...controllerField}
              placeholder={field.placeholder}
              disabled={field.disabled}
              min={field.min}
              max={field.max}
              step={field.step}
              onChange={async (value: number | undefined) => {
                controllerField.onChange(value);
                if (field.validateOnChange && value !== undefined) {
                  await handleValidation(value, field.name);
                }
              }}
              onBlur={async () => {
                controllerField.onBlur();
                if (field.validateOnBlur !== false) {
                  await handleValidation(controllerField.value, field.name);
                }
              }}
            />
            {rightElement && (
              <Box position="absolute" right="12px" top="50%" transform="translateY(-50%)">
                {rightElement}
              </Box>
            )}
          </Box>
        );

      case 'textarea':
        return (
          <Box position="relative">
            <Textarea
              value={controllerField.value || ''}
              name={controllerField.name}
              ref={controllerField.ref}
              placeholder={field.placeholder}
              disabled={field.disabled}
              readOnly={field.readOnly}
              onChange={
                ((e: React.ChangeEvent<HTMLTextAreaElement>) => {
                  const value = e.target.value;
                  controllerField.onChange(value);
                  if (field.validateOnChange) {
                    handleValidation(value, field.name);
                  }
                }) as React.FormEventHandler
              }
              onBlur={() => {
                controllerField.onBlur();
                if (field.validateOnBlur !== false) {
                  handleValidation(controllerField.value, field.name);
                }
              }}
            />
            {rightElement && (
              <Box position="absolute" right="12px" top="12px">
                {rightElement}
              </Box>
            )}
          </Box>
        );

      case 'select':
        return (
          <Select
            {...controllerField}
            placeholder={field.placeholder || 'Select an option'}
            disabled={field.disabled}
            options={field.options || []}
            onChange={async (value: string) => {
              controllerField.onChange(value);
              if (field.validateOnChange) {
                await handleValidation(value, field.name);
              }
            }}
          />
        );

      case 'checkbox':
        return (
          <Checkbox
            {...controllerField}
            disabled={field.disabled}
            onChange={async (checked: boolean) => {
              controllerField.onChange(checked);
              if (field.validateOnChange) {
                await handleValidation(checked, field.name);
              }
            }}
          >
            {field.label}
          </Checkbox>
        );

      case 'radio':
        return (
          <RadioGroup
            {...controllerField}
            disabled={field.disabled}
            options={field.options || []}
            onChange={async (value: string) => {
              controllerField.onChange(value);
              if (field.validateOnChange) {
                await handleValidation(value, field.name);
              }
            }}
          />
        );

      default:
        return (
          <Text color="#EF4444" fontSize="sm">
            Unsupported field type: {field.type}
          </Text>
        );
    }
  };

  return (
    <Field.Root
      invalid={!!errors[field.name] || (field.asyncValidator && !!asyncValidation.error)}
      data-testid="field-root"
    >
      {field.label && field.type !== 'checkbox' && (
        <Box
          as="label"
          fontSize="sm"
          fontWeight="medium"
          color={
            !!errors[field.name] || (field.asyncValidator && !!asyncValidation.error)
              ? '#EF4444'
              : '#374151'
          }
          data-testid="field-label"
        >
          {field.label}
          {field.required && (
            <Box as="span" color="#EF4444" marginLeft="4px">
              *
            </Box>
          )}
        </Box>
      )}
      <Controller
        name={field.name as Path<T>}
        control={control}
        defaultValue={(field.defaultValue ?? (field.type === 'number' ? 0 : '')) as T[Path<T>]}
        render={({ field: controllerField }) => renderFieldContent(controllerField)}
      />
      {field.helperText &&
        !errors[field.name] &&
        !(field.asyncValidator && asyncValidation.error) && (
          <Box fontSize="sm" color="#6B7280" marginTop={1} data-testid="field-helper">
            {field.helperText}
          </Box>
        )}
      {(errors[field.name] || (field.asyncValidator && asyncValidation.error)) && (
        <Box fontSize="sm" color="#EF4444" marginTop={1} data-testid="field-error">
          {(field.asyncValidator && asyncValidation.error) ||
            (errors[field.name]?.message as string)}
        </Box>
      )}
    </Field.Root>
  );
}
